import { Link, useNavigate, useParams } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAdminUserEnrollments } from '@/hooks/use-admin'
import { useAuth } from '@/lib/auth'

export default function AdminUserEnrollmentsPage() {
  const { userId } = useParams<{ userId: string }>()
  const { user: authUser } = useAuth()
  const navigate = useNavigate()
  const tenantSlug = authUser?.tenantSlug ?? ''

  const { data, isLoading, error } = useAdminUserEnrollments(userId)

  const result = data as any

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate(`/${tenantSlug}/admin/enrollments`)}>
          受講状況管理に戻る
        </Button>
        <p className="text-destructive">エラーが発生しました: {(error as Error).message}</p>
      </div>
    )
  }

  const userInfo = result?.user
  const enrollments = result?.enrollments ?? []

  return (
    <div className="space-y-6">
      <Button variant="outline" onClick={() => navigate(`/${tenantSlug}/admin/enrollments`)}>
        受講状況管理に戻る
      </Button>

      {/* User info card */}
      <Card>
        <CardHeader>
          <CardTitle>ユーザー情報: {userInfo?.name || userId}</CardTitle>
          <CardDescription>
            ID: {userInfo?.id}
            {userInfo?.email && (
              <>
                <br />
                Email: {userInfo.email}
              </>
            )}
            {userInfo?.role && (
              <>
                <br />
                ロール: {userInfo.role}
              </>
            )}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Enrollments table */}
      <Card>
        <CardHeader>
          <CardTitle>受講コース一覧</CardTitle>
          <CardDescription>{enrollments.length}件のコースに登録されています。</CardDescription>
        </CardHeader>
        <CardContent>
          {enrollments.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>コース名</TableHead>
                  <TableHead>カテゴリ</TableHead>
                  <TableHead>ステータス</TableHead>
                  <TableHead>登録日</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrollments.map((item: any) => (
                  <TableRow key={item.enrollment.id}>
                    <TableCell>
                      <Link
                        to={`/${tenantSlug}/admin/enrollments/course/${item.course.id}`}
                        className="hover:underline text-primary"
                      >
                        {item.course.title || 'N/A'}
                      </Link>
                    </TableCell>
                    <TableCell>{item.course.category || '-'}</TableCell>
                    <TableCell>
                      <Badge
                        variant={item.enrollment.status === 'completed' ? 'default' : 'secondary'}
                      >
                        {item.enrollment.status === 'completed' ? '完了' : '受講中'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {item.enrollment.enrolledAt
                        ? new Date(item.enrollment.enrolledAt).toLocaleDateString('ja-JP')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(`/${tenantSlug}/admin/enrollments/course/${item.course.id}`)
                        }
                      >
                        講座詳細
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground">このユーザーはまだコースに登録していません。</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
